import type {
  DataBuilderCondition,
  DataBuilderModelVersions,
  DataBuilderSingleCondition,
} from './data-builder-api.types';

type ConditionContext = {
  models: Partial<Record<keyof DataBuilderModelVersions, number | string>>;
  settings?: Partial<Record<string, string | number | boolean>>;
  unitOfAnalysis?: string | null;
};

const MODEL_FIELD_PREFIX = 'model.';
const SETTING_FIELD_PREFIX = 'setting.';
const UNIT_OF_ANALYSIS_FIELD_PREFIX = 'unit_of_analysis.';

const getConditionActualValue = (
  field: DataBuilderSingleCondition['field'],
  context: ConditionContext
): string | number | undefined => {
  if (field.startsWith(MODEL_FIELD_PREFIX)) {
    const modelKey = field.slice(
      MODEL_FIELD_PREFIX.length
    ) as keyof DataBuilderModelVersions;

    return context.models[modelKey];
  }

  if (field.startsWith(SETTING_FIELD_PREFIX)) {
    const settingKey = field.slice(SETTING_FIELD_PREFIX.length);
    const value = context.settings?.[settingKey];

    return typeof value === 'boolean' ? undefined : value;
  }

  if (field.startsWith(UNIT_OF_ANALYSIS_FIELD_PREFIX)) {
    return context.unitOfAnalysis ?? undefined;
  }

  return undefined;
};

const isSingleCondition = (
  condition: DataBuilderCondition
): condition is DataBuilderSingleCondition => condition.type === 'comparison';

export const evaluateCondition = (
  condition: DataBuilderCondition | null | undefined,
  context: ConditionContext
): boolean => {
  if (condition === null || condition === undefined) {
    return true;
  }

  if (isSingleCondition(condition)) {
    const actualValue = getConditionActualValue(condition.field, context);

    if (actualValue === undefined) {
      return false;
    }

    const singleConditionResult = (() => {
      if (condition.operator === 'MAJOR_VERSION_EQUAL_TO') {
        return (
          typeof actualValue === 'number' &&
          typeof condition.value === 'number' &&
          Math.floor(actualValue) === condition.value
        );
      }

      if (condition.operator === 'NOT_EQUAL_TO') {
        return actualValue !== condition.value;
      }

      if (condition.operator === 'IN_SET') {
        return (
          Array.isArray(condition.value) &&
          condition.value.some((item) => item === actualValue)
        );
      }

      return actualValue === condition.value;
    })();

    return singleConditionResult;
  }

  if (condition.type === 'and') {
    const andResult = condition.conditions.every((nestedCondition) =>
      evaluateCondition(nestedCondition, context)
    );

    return andResult;
  }

  if (condition.type === 'or') {
    const orResult = condition.conditions.some((nestedCondition) =>
      evaluateCondition(nestedCondition, context)
    );

    return orResult;
  }

  return false;
};

export type { ConditionContext };
